import { useCityPageContent } from 'hooks/useCityPageContent';
import { CityPageLayout } from 'layouts/cityPage.layout';
import { RegionPageLayout } from 'layouts/regionPage.layout';
import { ZipPageLayout } from 'layouts/zipPage.layout';
import { chain, isNil, omitBy } from 'lodash';
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import React, { useMemo } from 'react';
import { BASE_PATH } from 'utils/constants';
import { CuisineFilterButton, generatePageCuisineButtons } from 'utils/cuisine-util';
import { DietaryFilterButton, generatePageDietaryButtons } from 'utils/dietary-util';
import { getFrontEndUrl } from 'utils/environment-util';
import { CityPageCityFragment, CityPageFilterFragment } from 'utils/gqlReactTypings.generated.d';
import {
  memoizedCitiesById,
  memoizedCitiesByRegionId,
  memoizedFiltersForDeliveryHubId,
  memoizedFiltersForHublessZip,
  memoizedUrlDetails,
} from 'utils/memoized-data-structure';
import {
  ComputedRegion,
  isRegionPage,
  isShallowCityPage,
  isZipPage,
  Location,
  PageObj,
  ShallowCityPage,
} from 'utils/types';
import { encodeCityPageHref, encodeZipPageHref, pathToHref } from 'utils/urls';

export const LINKS_BLOCK_LIMIT = 5;

interface IProps {
  pageObj: PageObj;
  region?: ComputedRegion;
  cityPage?: CityPageCityFragment;
  cuisineButtons?: CuisineFilterButton[];
  dietaryButtons?: DietaryFilterButton[];
  activeFilter?: CityPageFilterFragment;
}

const CityPage: React.FC<IProps> = ({ pageObj, region, cuisineButtons, dietaryButtons, activeFilter }) => {
  // Use memo to ensure object doesn't trigger re-renders
  const cityPageContentParams = useMemo(
    () => ({
      regionId: region?.id,
      zipCode: isShallowCityPage(pageObj) || isZipPage(pageObj) ? pageObj.zipCode : null,
      filter: activeFilter,
      loggableName: `${pageObj.__typename} [${pageObj.id}]`,
    }),
    [activeFilter, pageObj, region?.id]
  );
  const { shefs, dishes, numDishes } = useCityPageContent(cityPageContentParams);

  const canonicalHref = `${getFrontEndUrl()}${BASE_PATH}${pathToHref(pageObj.path)}`;
  const head = (
    <Head>
      <link rel='canonical' href={canonicalHref} />
    </Head>
  );

  if (isShallowCityPage(pageObj) && cuisineButtons && dietaryButtons) {
    // Note: Use passed-in cityPage since pageObj is shallow
    return (
      <>
        {head}
        <CityPageLayout
          cityPage={pageObj}
          dishes={dishes}
          shefs={shefs}
          totalDishesCount={numDishes}
          cuisineButtons={cuisineButtons}
          dietaryButtons={dietaryButtons}
          activeFilter={activeFilter}
        />
      </>
    );
  }
  if (isZipPage(pageObj) && cuisineButtons && dietaryButtons) {
    return (
      <>
        {head}
        <ZipPageLayout
          zipCode={pageObj.zipCode}
          zipPage={pageObj}
          dishes={dishes}
          shefs={shefs}
          totalDishesCount={numDishes}
          cuisineButtons={cuisineButtons}
          dietaryButtons={dietaryButtons}
          activeFilter={activeFilter}
        />
      </>
    );
  }

  if (isRegionPage(pageObj) && region) {
    return (
      <>
        {head}
        <RegionPageLayout region={region} dishes={dishes} shefs={shefs} />
      </>
    );
  }

  // Note: Should be unreachable. Any valid path *will* have data
  throw new Error('There should never be a content page without a valid page type');
};

export const getStaticPaths: GetStaticPaths = async () => {
  // Allow local builds without API by skipping SSG data fetches
  if (process.env.SKIP_SSG_FETCH === 'true' || process.env.NEXT_PUBLIC_SKIP_SSG === 'true') {
    return { paths: [], fallback: false };
  }
  const cityPagesByUrl = await memoizedUrlDetails();

  const paths = chain(cityPagesByUrl)
    .map('path') // Just pull the paths out
    .map((path) => ({
      params: {
        cityPage: path,
      },
    }))
    .value();

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<IProps, { cityPage: string[] }> = async ({ params }) => {
  if (process.env.SKIP_SSG_FETCH === 'true' || process.env.NEXT_PUBLIC_SKIP_SSG === 'true') {
    // Minimal props for build without network calls
    return {
      props: {
        pageObj: {
          __typename: 'RegionInfo',
          id: 0,
          name: 'Placeholder',
          path: ['placeholder'],
        } as any,
      },
    };
  }
  // Load/Re-compute memoized data structures
  const citiesById = await memoizedCitiesById();
  const pageObj = (await memoizedUrlDetails())[pathToHref(params?.cityPage)];
  if (!pageObj) {
    return { notFound: true };
  }
  const { filter: activeFilter } = pageObj;

  if (isShallowCityPage(pageObj) || isZipPage(pageObj)) {
    const cityPage: CityPageCityFragment = citiesById[pageObj.id];
    const { deliveryHubId, zipCode } = cityPage.zipCodeInfo;
    const filters: CityPageFilterFragment[] = isNil(deliveryHubId)
      ? await memoizedFiltersForHublessZip(zipCode)
      : await memoizedFiltersForDeliveryHubId(deliveryHubId);

    const cuisineButtons: CuisineFilterButton[] = generatePageCuisineButtons({ pageObj, filters, activeFilter });
    const dietaryButtons: DietaryFilterButton[] = generatePageDietaryButtons({ pageObj, filters, activeFilter });

    const props: IProps = {
      pageObj,
      cuisineButtons,
      dietaryButtons,
      ...omitBy({ activeFilter }, isNil), // Compact the optional fields
    };

    return { props };
  }

  if (isRegionPage(pageObj)) {
    const citiesByRegionId = await memoizedCitiesByRegionId();
    const regionCities: ShallowCityPage[] = activeFilter
      ? citiesByRegionId[pageObj.id].slice(0, LINKS_BLOCK_LIMIT)
      : citiesByRegionId[pageObj.id];
    const nearbyCities: Location[] = regionCities.map((city) => ({
      name: `${city.cityName}, ${city.stateNameShort}`,
      href: encodeCityPageHref(city),
    }));
    const nearbyZipCodes: Location[] = regionCities.map((city) => ({
      name: city.zipCode,
      href: encodeZipPageHref(city, city.zipCode),
    }));

    const region: ComputedRegion = { ...pageObj, nearbyCities, nearbyZipCodes };
    const props: IProps = {
      pageObj,
      region,
      ...omitBy({ activeFilter }, isNil),
    };

    return { props };
  }

  // Note: Should be unreachable. Any valid path *will* have data
  throw new Error('There should never be a content page without a valid page type');
};

export default CityPage;
